import { Button, Typography, TextField, Box, Switch, SwitchLabel } from '@barracuda-internal/bds-core-v2';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect } from 'react';
import LoadingIndicator from 'src/lib/components/LoadingIndicator';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useEmailSettingsLogic } from './useEmailSettingsLogic';
import ToastComponent from 'src/components/toast/ToastComponent';
import useToast from 'src/hooks/useToast';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useAppDispatch } from 'src/hooks/useAppDispatch';
import { pushToast } from 'src/features/notifications/notificationsSlice';

const useStyles = makeStyles(() => ({
	section: {
		padding: '16px',
	},
}));


const EmailSettingsForm = () => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const { toasts, triggerToast } = useToast();

    const toast = useAppSelector((_state) => _state.notifications.toast);

    const {
        formState,
        validEmail,
        emailError,
        handleSaveClick,
        handleFieldChange,
        handleEmailBlur,
        loading,
        sendDigests,
        sendEndUserAlerts,
        showSenders,
        emailDestinations,
        sendRemediationAlerts,
        alertsBatchingHourlyInterval,
    } = useEmailSettingsLogic();

    useEffect(() => {
        if (toast.message.length > 0) {
            const { message, type } = toast;
            triggerToast({
                type: type,
                message: message,
                duration: 3000,
            });

            dispatch(
                pushToast({
                    type: 'success',
                    message: '',
                }),
            );
        }
    }, [toast, triggerToast, dispatch]);

    if (!formState) return <LoadingIndicator />;

    return (
        <>
            <Typography variant="body1" fontWeight={700}>Administrator Emails</Typography>
            <section className={classes.section}>
                <span data-intercom-target="Send Admin Email Digests Toggle">
                    <SwitchLabel
                        label={'Send a daily email digest to all admins'}
                        sx={{ gap: 1 }}
                        control={
                            <Switch
                                color="primary"
                                checked={sendDigests || false}
                                onChange={(e) =>
                                    handleFieldChange(
                                        'sendDigests',
                                        e.target.checked,
                                    )
                                }
                            />
                        }
                    />
                </span>
                {sendDigests ? (
                    <Box sx={{ mt: 3 }}>
                        <span data-intercom-target="Send Custom Digest Toggle">
                            <SwitchLabel
                                label={
                                    'Additional recipients for the daily email digest'
                                }
                                sx={{ gap: 1 }}
                                control={
                                    <Switch
                                        color="primary"
                                        checked={showSenders}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                'showSenders',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                }
                            />
                        </span>
                    </Box>
                ) : null}
                {showSenders && sendDigests ? (
                    <Box sx={{ mt: 3 }}>
                        <Box>
                            <TextField
                                fullWidth
                                multiline
                                label="Admin email addresses to receive daily digest"
                                placeholder="Enter email addresses separated by commas (max 30)"
                                onChange={(e) =>
                                    handleFieldChange(
                                        'emailDestinations',
                                        e.target.value,
                                    )
                                }
                                onBlur={handleEmailBlur}
                                value={emailDestinations}
                                error={!validEmail}
                                helperText={!validEmail ? emailError : ''}
                            />
                        </Box>
                    </Box>
                ) : null}
            </section>
            <Box sx={{ mt: 1 }}>
                <Typography variant="body1" fontWeight={700}>End-User Notifications</Typography>
            </Box>
            <section className={classes.section}>
                <Box data-intercom-target="Send End User Email Toggle">
                    <SwitchLabel
                        label={'Send violation alerts to end users'}
                        sx={{ gap: 1 }}
                        control={
                            <Switch
                                color="primary"
                                checked={sendEndUserAlerts || false}
                                onChange={(e) =>
                                    handleFieldChange(
                                        'sendEndUserAlerts',
                                        e.target.checked,
                                    )
                                }
                            />
                        }
                    />
                </Box>

                <Box sx={{ mt: 3 }} data-intercom-target="Send End User Remediation Toggle">
                    <SwitchLabel
                        label={'Send remediation notifications to end users'}
                        sx={{ gap: 1}}
                        control={
                            <Switch
                                color="primary"
                                checked={sendRemediationAlerts || false}
                                onChange={(e) =>
                                    handleFieldChange(
                                        'sendRemediationAlerts',
                                        e.target.checked,
                                    )
                                }
                            />
                        }
                    />
                </Box>
            </section>

            <Box sx={{ mt: 1 }}>
                <Typography variant="body1" fontWeight={700}>
                    Enable batching in end-user notifications with interval
                </Typography>
            </Box>
            <section className={classes.section}>
                <FormControl sx={{ m: 1, minWidth: 300 }} size="small">
                    <Select
                        labelId="batch-select-label"
                        id="batch-simple-select"
                        value={alertsBatchingHourlyInterval}
                        onChange={(e) =>
                            handleFieldChange(
                                'alertsBatchingHourlyInterval',
                                Number(e.target.value),
                            )
                        }
                        disabled={!sendRemediationAlerts && !sendEndUserAlerts}
                    >
                        <MenuItem value={0}>Realtime</MenuItem>
                        <MenuItem value={1}>1 hour</MenuItem>
                        <MenuItem value={3}>3 hours</MenuItem>
                        <MenuItem value={6}>6 hours</MenuItem>
                        <MenuItem value={12}>12 hours</MenuItem>
                    </Select>
                </FormControl>
            </section>

            <section style={{ marginTop: '8px' }}>
                <footer>
                    <Button
                        onClick={handleSaveClick}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {'Save'}
                    </Button>
                </footer>
            </section>
            <ToastComponent toasts={toasts} position={'bottom-left'} />
        </>
    );
};

export default EmailSettingsForm;
							
